import React, { useState, useCallback } from 'react';
import { ChevronUp, ChevronDown, Type, Hash, CheckSquare, SquareStack, Box, CircleDot } from 'lucide-react';
import { PlaybookTransformItem, PlaybookExpression, PlaybookFilter, PlaybookEventConfig } from '@/types/playbook';
import { UnifiedSchema, EventSchema } from '@/types/schema';
import { EditorSelection } from '../PlaybookEditorTab';
import PlaybookTransformTable from '@/components/playbooks/PlaybookTransformTable';
import PlaybookTransformCard from './PlaybookTransformCard';
import { getTransformDisplayName, getTransformDetailDisplay, getMappingTypeDisplay, truncatePath, handleSort, isPropertyPairApplicableToAllEvents as isPropertyPairApplicableToAllEventsUtil } from '../PlaybookTransformUtils';
// Minor change to trigger TS re-evaluation
export type PlaybookViewMode = 'card' | 'table';

interface PlaybookTransformListProps {
  transforms: PlaybookTransformItem[];
  selectedTransform: PlaybookTransformItem | null;
  onTransformSelect: (transformId: string) => void;
  onDeleteTransform: (transformId: string) => void;
  onUpdateTransform: (transformId: string, updates: Partial<PlaybookTransformItem>) => void; // Added
  onSelectionChange: (selection: EditorSelection) => void; // Added
  inputSchema: UnifiedSchema | null;
  outputSchema: UnifiedSchema | null;
  allEvents: EventSchema[];
  uiEventConfigs: PlaybookEventConfig[]; // Added
  allowUndefinedTransforms: boolean; // Added
  searchTerm: string; // Added as prop
  viewMode: PlaybookViewMode; // Added as prop
  selection: EditorSelection; // Add selection prop
  allUiTransforms: PlaybookTransformItem[]; // Add allUiTransforms prop
}

const PlaybookTransformList: React.FC<PlaybookTransformListProps> = ({
  transforms, // Now using this prop for filtering and sorting
  selectedTransform,
  onTransformSelect,
  onDeleteTransform,
  onUpdateTransform, // Added
  onSelectionChange, // Added
  inputSchema,
  outputSchema,
  allEvents,
  uiEventConfigs, // Added
  allowUndefinedTransforms, // Added
  searchTerm, // Destructure from props
  viewMode, // Destructure from props
  selection, // Destructure selection prop
  allUiTransforms, // Destructure allUiTransforms prop
}) => {
  
  const [sortBy, setSortBy] = useState<'inputKey' | 'transformType' | 'outputKey' | 'mappingType' | 'events'>('inputKey');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const filteredAndSortedTransforms = transforms // Use 'transforms' prop directly
    .filter(transform => {
      // First, filter by selection scope

      if (selection.type === 'global') {
        const isGlobal = transform.appliesToScope === 'global';
        if (!isGlobal) {
          return false;
        }
      } else if (selection.type === 'event' && selection.eventId) {
        const isEventSpecific = transform.appliesToScope === 'eventSpecific';
        const hasEventId = transform.eventIds?.includes(selection.eventId);
        const passes = isEventSpecific && hasEventId;
        if (!passes) {
          return false;
        }
      }
      return true;
    })
    .filter(transform => {
      const searchLower = searchTerm.toLowerCase();
      const displayName = getTransformDisplayName(transform.type).toLowerCase();
      const inputKey = transform.inputPath?.toLowerCase() || '';
      const outputKey = transform.outputPath?.toLowerCase() || '';
      const expressionBody = (transform.content as PlaybookExpression)?.body?.toLowerCase() || '';
      const filterRules = typeof (transform.content as PlaybookFilter)?.rules === 'string' 
        ? (transform.content as PlaybookFilter).rules.toLowerCase() 
        : '';

      return (
        displayName.includes(searchLower) ||
        inputKey.includes(searchLower) ||
        outputKey.includes(searchLower) ||
        expressionBody.includes(searchLower) ||
        filterRules.includes(searchLower)
      );
    })
    .sort((a, b) => {
      if (sortBy === 'transformType') {
        const nameA = getTransformDisplayName(a.type);
        const nameB = getTransformDisplayName(b.type);
        return sortDirection === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
      } else if (sortBy === 'events') {
        const eventsA = a.eventUsageCount || 0; // Use eventUsageCount from PlaybookTransformItem
        const eventsB = b.eventUsageCount || 0;
        return sortDirection === 'asc' ? eventsA - eventsB : eventsB - eventsA;
      } else if (sortBy === 'inputKey') {
        const valA = a.inputPath || '';
        const valB = b.inputPath || '';
        return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      } else if (sortBy === 'outputKey') {
        const valA = a.outputPath || '';
        const valB = b.outputPath || '';
        return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      } else if (sortBy === 'mappingType') {
        const valA = a.mappingType || '';
        const valB = b.mappingType || '';
        return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }

      return 0;
    });

  const handleLocalSort = useCallback((column: typeof sortBy) => {
    handleSort(column, sortBy, sortDirection, setSortBy, setSortDirection);
  }, [sortBy, sortDirection]);

  const getTypeIcon = useCallback((dataType?: string) => {
    switch (dataType) {
      case 'string': return <Type size={14} />;
      case 'number': return <Hash size={14} />;
      case 'boolean': return <CheckSquare size={14} />;
      case 'array': return <SquareStack size={14} />;
      case 'object': return <Box size={14} />;
      default: return <CircleDot size={14} />;
    }
  }, []);


  const SortableHeader = ({ column, children }: { column: typeof sortBy; children: React.ReactNode }) => (
    <button
      onClick={() => handleLocalSort(column)}
      className="flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
    >
      {children}
      {sortBy === column && (
        sortDirection === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
      )}
    </button>
  );

  return (
    <div className="w-full flex flex-col flex-1">
      {/* The header content (view toggle and search) is now in PlaybookTransformEditorPanelHeader */}
      <div className="px-2">
        {viewMode === 'card' ? (
          <>
            {/* Sortable Header for Card View */}
            {filteredAndSortedTransforms.length > 0 && (
              <div className="flex items-center p-2 mb-2">
                <div className="w-[25%]">
                  <SortableHeader column="inputKey">Input Key</SortableHeader>
                </div>
                <div className="w-[25%]">
                  <SortableHeader column="transformType">Transform</SortableHeader>
                </div>
                <div className="w-[25%]">
                  <SortableHeader column="outputKey">Output Key</SortableHeader>
                </div>
                <div className="w-[15%]">
                  <SortableHeader column="mappingType">Matching</SortableHeader>
                </div>
                <div className="w-[10%] flex justify-end">
                  <SortableHeader column="events">Events</SortableHeader>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              {filteredAndSortedTransforms.length === 0 && searchTerm && (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg">
                  No transforms found matching "{searchTerm}"
                </div>
              )}
              {filteredAndSortedTransforms.map((transform) => (
                <PlaybookTransformCard
                  key={transform.id}
                  transform={transform}
                  isSelected={selectedTransform?.id === transform.id}
                  onTransformSelect={onTransformSelect}
                  onDeleteTransform={onDeleteTransform}
                  onUpdateTransform={onUpdateTransform} // Added
                  onSelectionChange={onSelectionChange} // Added
                  inputSchema={inputSchema}
                  outputSchema={outputSchema}
                  allEvents={allEvents}
                  uiEventConfigs={uiEventConfigs} // Added
                  allowUndefinedTransforms={allowUndefinedTransforms} // Added
                  getTransformDisplayName={getTransformDisplayName}
                  getTransformDetailDisplay={getTransformDetailDisplay}
                  getMappingTypeDisplay={getMappingTypeDisplay}
                  getTypeIcon={getTypeIcon}
                  truncatePath={truncatePath}
                  isPropertyPairApplicableToAllEvents={(inputPath, outputPath) => isPropertyPairApplicableToAllEventsUtil(inputPath, outputPath, allEvents)}
                  allUiTransforms={allUiTransforms} // Pass allUiTransforms
                />
              ))}
            </div>
          </>
        ) : (
          /* Table View */
          <PlaybookTransformTable
            transforms={filteredAndSortedTransforms}
            selectedTransform={selectedTransform}
            isEditable={true}
            onTransformSelect={onTransformSelect}
            onDeleteTransform={onDeleteTransform}
            onUpdateTransform={onUpdateTransform} // Added
            onSelectionChange={onSelectionChange} // Added
            inputSchema={inputSchema}
            outputSchema={outputSchema}
            allEvents={allEvents}
            uiEventConfigs={uiEventConfigs} // Added
            allowUndefinedTransforms={allowUndefinedTransforms} // Added
            sortBy={sortBy}
            sortDirection={sortDirection}
            handleSort={handleLocalSort}
            getTransformDisplayName={getTransformDisplayName}
            getTransformDetailDisplay={getTransformDetailDisplay}
            getMappingTypeDisplay={getMappingTypeDisplay}
            getTypeIcon={getTypeIcon}
            truncatePath={truncatePath}
            isPropertyPairApplicableToAllEvents={(inputPath, outputPath) => isPropertyPairApplicableToAllEventsUtil(inputPath, outputPath, allEvents)}
            allUiTransforms={allUiTransforms} // Pass allUiTransforms
          />
        )}
      </div>
    </div>
  );
};

export default PlaybookTransformList;
